import { useState, useCallback } from 'react';
import { useQueryClient } from 'react-query';
import useHttpMutation from '@actions/services/firestore/browser/useHttpMutation';
import type { UseRemoveFolderResult, EntityType } from './types';

interface UseRemoveFolderParams {
    agentId: string;
    entityType: EntityType;
}

interface RemoveFolderResponse {
    success: boolean;
}

const useRemoveFolder = ({ agentId, entityType }: UseRemoveFolderParams): UseRemoveFolderResult => {
    const queryClient = useQueryClient();
    const [error, setError] = useState<Error | undefined>();
    const [isRemoving, setIsRemoving] = useState(false);

    const { mutateAsync } = useHttpMutation<void, RemoveFolderResponse>({
        method: 'DELETE',
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [`folders-${agentId}-${entityType}`] });
        },
        onError: (err: Error) => {
            setError(err);
        },
    });

    const removeFolder = useCallback(
        async (folderId: string): Promise<void> => {
            setError(undefined);
            setIsRemoving(true);
            try {
                await mutateAsync({
                    pathname: `/folders/${folderId}`,
                });
            } finally {
                setIsRemoving(false);
            }
        },
        [mutateAsync]
    );

    return {
        removeFolder,
        isRemoving,
        error,
    };
};

export default useRemoveFolder;
